// src/utils/captionFilter.ts
//
// Client-side filter applied to custom caption text in ZodiacPhotoFrame.
//
// Caption text is burned into the poster's own image and is never stored on
// Crushhh servers or displayed to other users. Client-side enforcement is
// sufficient here; a server-side check would only be warranted if caption
// content were persisted or broadcast to other users.

export const CAPTION_MAX_CHARS = 50;

// Letters, digits, space, apostrophe, and basic punctuation only.
// Everything else — emoji, zero-width joiners, non-printing codepoints —
// is stripped before any further check.
// \p{L} = any Unicode letter, \p{N} = any Unicode number (u flag required).
// Keeps accented Latin (é, ñ, ü), non-Latin scripts, and digits from any script
// while still stripping emoji, zero-width joiners, symbols, and control chars.
const ALLOWED_RE = /[^\p{L}\p{N} ',.!\-?]/gu;

// Common leetspeak digit substitutions. Applied to the cleaned input before
// blocklist comparison so "n1gg3r", "f4gg0t", etc. collapse to base form.
function normalizeLeet(s: string): string {
  return s
    .replace(/0/g, "o")
    .replace(/1/g, "i")
    .replace(/3/g, "e")
    .replace(/4/g, "a")
    .replace(/5/g, "s");
}

// Terms ≥5 characters — safe for plain substring matching since they do not
// appear as substrings in common English words after allowlist stripping.
// Checked against both the cleaned text and its leet-normalized form.
const SUBSTR_TERMS: readonly string[] = [
  // Racial and ethnic slurs
  "nigger", "nigga", "nigg",
  "chink",
  "wetback",
  "beaner",
  "raghead",
  "towelhead",
  "redskin",
  "kike",
  "hymie",
  "zipperhead",
  // Homophobic and transphobic slurs
  "faggot",
  "tranny",
  "shemale",
  // Other derogatory terms
  "retard",
];

// Short terms checked with word boundaries only — bare substring matching
// would false-positive on innocent words (e.g. "coon" in "raccoon",
// "jap" in "japan").
const WORD_BOUNDARY_RE = new RegExp(
  `\\b(${[
    "jap", "japs",
    "gook", "gooks",
    "coon",
    "spic", "spics",
    "fag", "fags",
    "dyke", "dykes",
  ].join("|")})\\b`,
);

export type FilterResult =
  | { ok: true; value: string }
  | { ok: false; reason: string };

export function filterCaption(raw: string): FilterResult {
  // 1. Hard cap — slice before normalization so length is always ≤ max.
  const capped = raw.slice(0, CAPTION_MAX_CHARS);

  // 2. NFKC normalization — collapses Unicode lookalike attacks (fullwidth
  //    latin letters, homoglyph substitutions) to their canonical base forms.
  const nfkc = capped.normalize("NFKC");

  // 3. Strip non-allowed characters; collapse whitespace.
  const stripped = nfkc.replace(ALLOWED_RE, "").replace(/\s+/g, " ").trim();

  // 4. Re-check length after stripping (defensive; stripping only shortens).
  if (stripped.length > CAPTION_MAX_CHARS) {
    return { ok: false, reason: "Please keep it under 50 characters." };
  }

  const lower = stripped.toLowerCase();
  const leet  = normalizeLeet(lower);

  // 5. Substring check — long unambiguous terms, tested on both cleaned and
  //    leet-normalized forms to catch digit-substitution variants.
  for (const term of SUBSTR_TERMS) {
    if (lower.includes(term) || leet.includes(term)) {
      return { ok: false, reason: "Keep it friendly." };
    }
  }

  // 6. Word-boundary check — short terms that would match innocent substrings
  //    without boundary enforcement.
  if (WORD_BOUNDARY_RE.test(lower) || WORD_BOUNDARY_RE.test(leet)) {
    return { ok: false, reason: "Keep it friendly." };
  }

  return { ok: true, value: stripped };
}
